import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface ProfilData {
  id: number;
  adi: string;
  soyadi: string;
  email: string;
  rol: string;
}

interface SifreFormData {
  mevcutSifre: string;
  yeniSifre: string;
  yeniSifreTekrar: string;
}

const KullaniciProfil: React.FC = () => {
  const { user } = useAuth();
  const [profil, setProfil] = useState<ProfilData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sifreFormu, setSifreFormu] = useState<SifreFormData>({
    mevcutSifre: '',
    yeniSifre: '',
    yeniSifreTekrar: ''
  });
  const [sifreLoading, setSifreLoading] = useState(false);

  useEffect(() => {
    fetchProfil();
  }, []);

  const fetchProfil = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/Kullanicilar/${user?.kullaniciID}`);
      setProfil(response.data);
    } catch (error) {
      console.error('Profil yüklenirken hata:', error);
      toast.error('Profil bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSifreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSifreFormu(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSifreDegistir = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validasyon
    if (!sifreFormu.mevcutSifre || !sifreFormu.yeniSifre || !sifreFormu.yeniSifreTekrar) {
      toast.error('Tüm alanları doldurunuz');
      return;
    }

    if (sifreFormu.yeniSifre !== sifreFormu.yeniSifreTekrar) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    if (sifreFormu.yeniSifre.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      setSifreLoading(true);
      await axios.post(`/api/Kullanicilar/SifreDegistir`, {
        kullaniciId: user?.kullaniciID,
        mevcutSifre: sifreFormu.mevcutSifre,
        yeniSifre: sifreFormu.yeniSifre
      });

      toast.success('Şifreniz başarıyla değiştirildi');
      setSifreFormu({
        mevcutSifre: '',
        yeniSifre: '',
        yeniSifreTekrar: ''
      });
    } catch (error: any) {
      console.error('Şifre değiştirme hatası:', error);
      if (error.response?.status === 400) {
        toast.error('Mevcut şifreniz hatalı');
      } else {
        toast.error('Şifre değiştirilirken hata oluştu');
      }
    } finally {
      setSifreLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <style>{`
          .pf-page { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; }
        `}</style>
        <div className="pf-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#cd6906', animation: 'pf-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            <span style={{ color: '#94a3b8', fontSize: 13 }}>Yükleniyor…</span>
          </div>
          <style>{`@keyframes pf-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes pf-spin { to { transform: rotate(360deg); } }

        .pf-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          padding: 24px;
        }

        /* ── Page header ── */
        .pf-page-header {
          padding-bottom: 20px;
          margin-bottom: 24px;
          border-bottom: 1px solid #f3f4f6;
        }
        .pf-page-title { font-size: 20px; font-weight: 600; color: #111827; margin: 0; }
        .pf-page-sub   { font-size: 13px; color: #6b7280; margin: 3px 0 0; }

        /* ── Card ── */
        .pf-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 28px 32px;
          margin-bottom: 20px;
        }
        .pf-card-head { margin-bottom: 24px; }
        .pf-card-title {
          font-size: 15px; font-weight: 600; color: #111827; margin: 0 0 4px;
          display: flex; align-items: center; gap: 9px;
        }
        .pf-card-title svg { width: 17px; height: 17px; stroke-width: 1.8; fill: none; stroke-linecap: round; stroke-linejoin: round; }
        .pf-card-desc { font-size: 13px; color: #6b7280; margin: 0; }

        /* ── Info grid (read-only) ── */
        .pf-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px 32px; }
        .pf-info-item { display: flex; flex-direction: column; gap: 5px; }
        .pf-info-label {
          font-size: 10px; font-weight: 600; letter-spacing: 0.07em;
          text-transform: uppercase; color: #9ca3af;
        }
        .pf-info-value { font-size: 14px; font-weight: 500; color: #111827; }

        /* ── Alert ── */
        .pf-alert {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 12px 14px; border-radius: 10px;
          background: #fff8f0; border: 1px solid #fcd9a0;
          margin-top: 24px;
        }
        .pf-alert svg { width: 15px; height: 15px; stroke: #cd6906; stroke-width: 2; fill: none; flex-shrink: 0; margin-top: 1px; }
        .pf-alert-text { font-size: 12px; color: #92400e; line-height: 1.5; }

        /* ── Divider ── */
        .pf-divider { border: none; border-top: 1px solid #f3f4f6; margin: 28px 0; }

        /* ── Form fields ── */
        .pf-field { display: flex; flex-direction: column; gap: 6px; }
        .pf-field label {
          font-size: 11px; font-weight: 600; letter-spacing: 0.06em;
          text-transform: uppercase; color: #6b7280;
        }
        .pf-field label .pf-req { color: #f43f5e; }
        .pf-field input {
          width: 100%; padding: 10px 14px;
          border: 1px solid #d1d5db; border-radius: 8px;
          font-size: 13.5px; font-family: inherit; color: #111827;
          outline: none; box-sizing: border-box; background: #fff;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .pf-field input:focus {
          border-color: #111827;
          box-shadow: 0 0 0 3px rgba(17,24,39,0.08);
        }

        /* ── Mismatch ── */
        .pf-mismatch {
          display: flex; align-items: center; gap: 7px;
          padding: 9px 13px; border-radius: 8px;
          background: #fff1f2; border: 1px solid #fecdd3;
        }
        .pf-mismatch-dot { width: 6px; height: 6px; border-radius: 50%; background: #f43f5e; flex-shrink: 0; }
        .pf-mismatch-text { font-size: 12px; color: #be123c; font-weight: 500; }

        /* ── Save button ── */
        .pf-submit-btn {
          padding: 10px 22px; border: none; border-radius: 8px;
          background: #cd6906; color: #fff; font-size: 13.5px; font-weight: 600;
          font-family: inherit; cursor: pointer;
          display: inline-flex; align-items: center; gap: 7px;
          box-shadow: 0 1px 3px rgba(205,105,6,0.3);
          transition: background 0.15s, box-shadow 0.15s;
        }
        .pf-submit-btn:hover:not(:disabled) {
          background: #a85505;
          box-shadow: 0 4px 12px rgba(205,105,6,0.35);
        }
        .pf-submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }
        .pf-submit-btn svg { width: 15px; height: 15px; stroke: #fff; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; }
        .pf-spinner {
          width: 13px; height: 13px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: #fff; border-radius: 50%;
          animation: pf-spin 0.7s linear infinite;
        }

        @media (max-width: 768px) {
          .pf-page { padding: 16px; }
          .pf-card { padding: 20px; }
          .pf-info-grid { grid-template-columns: 1fr; }
          .pf-sifre-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="pf-page">

        {/* Page Header */}
        <div className="pf-page-header">
          <h1 className="pf-page-title">Profil Bilgilerim</h1>
          <p className="pf-page-sub">Hesap bilgilerinizi ve güvenlik ayarlarınızı yönetin.</p>
        </div>

        {/* ── Kullanıcı Bilgileri ── */}
        <div className="pf-card">
          <div className="pf-card-head">
            <h2 className="pf-card-title">
              <svg viewBox="0 0 24 24" stroke="#6b7280"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Kullanıcı Bilgileri
            </h2>
            <p className="pf-card-desc">Kişisel bilgileriniz ve hesap detaylarınız.</p>
          </div>

          <div className="pf-info-grid">
            <div className="pf-info-item">
              <span className="pf-info-label">Ad</span>
              <span className="pf-info-value">{profil?.adi || '—'}</span>
            </div>
            <div className="pf-info-item">
              <span className="pf-info-label">Soyad</span>
              <span className="pf-info-value">{profil?.soyadi || '—'}</span>
            </div>
            <div className="pf-info-item">
              <span className="pf-info-label">E-posta</span>
              <span className="pf-info-value">{profil?.email || '—'}</span>
            </div>
            <div className="pf-info-item">
              <span className="pf-info-label">Rol</span>
              <span className="pf-info-value">{profil?.rol || '—'}</span>
            </div>
          </div>

          <div className="pf-alert">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <span className="pf-alert-text">Bu bilgiler sadece görüntülenebilir. Değişiklik için sistem yöneticinize başvurunuz.</span>
          </div>
        </div>

        {/* ── Şifre Değiştir ── */}
        <div className="pf-card">
          <div className="pf-card-head">
            <h2 className="pf-card-title">
              <svg viewBox="0 0 24 24" stroke="#6b7280"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Şifre Değiştir
            </h2>
            <p className="pf-card-desc">Hesabınızın güvenliği için şifrenizi düzenli olarak güncelleyin.</p>
          </div>

          <form onSubmit={handleSifreDegistir}>
            <div className="pf-sifre-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div className="pf-field">
                <label>Mevcut Şifre <span className="pf-req">*</span></label>
                <input
                  type="password"
                  name="mevcutSifre"
                  value={sifreFormu.mevcutSifre}
                  onChange={handleSifreChange}
                  placeholder="Mevcut şifreniz"
                  required
                />
              </div>
              <div className="pf-field">
                <label>Yeni Şifre <span className="pf-req">*</span></label>
                <input
                  type="password"
                  name="yeniSifre"
                  value={sifreFormu.yeniSifre}
                  onChange={handleSifreChange}
                  placeholder="En az 6 karakter"
                  required
                  minLength={6}
                />
              </div>
              <div className="pf-field">
                <label>Yeni Şifre (Tekrar) <span className="pf-req">*</span></label>
                <input
                  type="password"
                  name="yeniSifreTekrar"
                  value={sifreFormu.yeniSifreTekrar}
                  onChange={handleSifreChange}
                  placeholder="Şifreyi tekrarlayın"
                  required
                />
              </div>
            </div>

            {sifreFormu.yeniSifre && sifreFormu.yeniSifreTekrar &&
             sifreFormu.yeniSifre !== sifreFormu.yeniSifreTekrar && (
              <div className="pf-mismatch" style={{ marginBottom: 16 }}>
                <span className="pf-mismatch-dot" />
                <span className="pf-mismatch-text">Şifreler eşleşmiyor!</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="pf-submit-btn" disabled={sifreLoading}>
                {sifreLoading ? (
                  <><span className="pf-spinner" /> Değiştiriliyor...</>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
                    Şifreyi Değiştir
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

      </div>
    </>
  );
};

export default KullaniciProfil;
